import { FC, JSX, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import {
  $CurrentDiscoverDataAsAvailable,
  $CurrentDiscoverKeyword,
  $CurrentDiscoverStandardKeyword,
} from '@discover/shared/atom';
import _uniqBy from 'lodash/uniqBy';
import { Avatar } from '~/components/avatar';
import Ellipsis from 'icon/ellipsis_horizontal_fill.svg';
import { Link } from '@remix-run/react';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useDiscoverDescV2 } from '@discover/shared/use-discover-desc';
import { ModuleType } from '~/utils/module-type';
import { useActiveToSearch } from '~/utils/active/use-active-to-search';
import { ClickPosition } from '~/utils/click-position';
import { useViewTracking } from '~/utils/use-view-tracking';

import './style.scss';

export const DiscoverInfoV2: FC = () => {
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const standardKeyword = useAtomValue($CurrentDiscoverStandardKeyword);
  const discoverData = useAtomValue($CurrentDiscoverDataAsAvailable);
  const description = useDiscoverDescV2();
  const activeToSearch = useActiveToSearch(standardKeyword);

  const coCreatorsObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.co_creator,
      });

      const { items } = discoverData.article_v2;
      const distinctItems = _uniqBy(items, (item) => item.author.userId).slice(0, 3);

      distinctItems.forEach((item) => {
        window.Tea('wap_cta_show', {
          current_page_module: ModuleType.co_creator,
          current_position: ClickPosition.co_creator,
          media_id: item.author.userId,
        });
      });
    }, []),
  });

  const handleCreatorClick = useCallback((event: SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const userId = event.currentTarget.dataset.userId;

    window.Tea('wap_click', {
      current_page_module: ModuleType.co_creator,
      current_position: ClickPosition.co_creator,
      is_cta: 1,
      media_id: userId,
    });
    activeToSearch(ClickPosition.co_creator, userId);
  }, []);

  /**
   * To implement the stack effect, we need to:
   * 1. put all avatar into an array.
   * 2. render in reversed order.
   * 3. use flex-direction: row-reverse.
   */
  const avatarElements: JSX.Element[] = useMemo(() => {
    const { items } = discoverData.article_v2;
    const distinctItems = _uniqBy(items, (item) => item.author.userId).slice(0, 3);

    if (!distinctItems.length) {
      return [];
    }

    const res = distinctItems.map(({ author }) => (
      <object>
        <Link to={`/${author.linkName}?region=${region}`} onClick={handleCreatorClick}>
          <Avatar
            key={author.userId}
            src={author.avatar}
            alt={t('own_picture').replace('%s', author.nickName)}
            data-user-id={author.userId}
          />
        </Link>
      </object>
    ));

    res.push(
      <div className="ellipsis">
        <Ellipsis />
      </div>,
    );

    return res.reverse();
  }, [discoverData]);

  return (
    <div className="discover-info-v2">
      <h1>{standardKeyword}</h1>
      <p className="description">{description}</p>
      {Boolean(avatarElements.length) && (
        <div className="posted-by" ref={coCreatorsObservedRef}>
          <span>{t('seo_keyword_page_postedBy')}</span>
          <ul className="authors">
            {avatarElements.map((element, index) => (
              <li className="author" key={index}>
                {element}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
